import { useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'

import { caseSelector, themeAtom } from '@/atoms'
import { Button, Dropdown, Input, TinyMCE } from '@/components'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import Icon from '@/components/priority/Icon/Icon'
import { useServiceTemplates } from '@/hooks/editor/useServiceTemplates'
import { SERVICE_TEMPLATE_CATEGORY_ORDER } from '@messenger/types'
import {
  DARK_PREVIEW_BG,
  LIGHT_PREVIEW_BG,
  BackgroundToggle,
} from './ServiceTemplates.styled'

import * as S from '../InsertMenu/SlideoutMenu/SlideoutMenu.styled'

type ServiceTemplateCreateProps = {
  setCreatingTemplate: (creating: boolean) => void
}

const ServiceTemplateCreate = ({
  setCreatingTemplate,
}: ServiceTemplateCreateProps) => {
  const editorRef = useRef<Editor | null>(null)
  const { t } = useTranslation()
  const caseDetails = useRecoilValue(caseSelector)
  const theme = useRecoilValue(themeAtom)
  const isDarkTheme = theme === 'dark'
  const [invertedBg, setInvertedBg] = useState(false)
  const isDarkBg = isDarkTheme ? !invertedBg : invertedBg

  const {
    templateName,
    setTemplateName,
    templateCategory,
    setTemplateCategory,
    isCreating,
    handleCreateTemplate,
  } = useServiceTemplates({
    editorRef,
    onCreateSuccess: () => setCreatingTemplate(false),
  })

  const categoryItems = Object.fromEntries(
    SERVICE_TEMPLATE_CATEGORY_ORDER.map((cat) => [
      cat,
      t(`editor.serviceTemplates.categories.${cat}`),
    ]),
  )

  const setTinyMCERef = (ref: Editor | null) => {
    if (!ref) return
    editorRef.current = ref
  }

  const handleCancel = () => {
    setCreatingTemplate(false)
  }

  return (
    <Flex column width="100%" height="100%">
      {/* @ts-expect-error - overflowY/overflowX are valid props in styled component */}
      <S.Content column flex="1" overflowY="auto" overflowX="hidden">
        <Flex column padding="0 32px 32px 32px" gap="20px">
          <Flex column gap="8px">
            <Input
              // @ts-expect-error - Input component value prop not properly typed
              value={[templateName, setTemplateName]}
              placeholder={t('editor.serviceTemplates.enterTemplateName')}
              label={t('editor.serviceTemplates.templateNameLabel')}
            />
          </Flex>
          <Flex column gap="8px">
            <Dropdown
              full
              width="100%"
              label={t('editor.serviceTemplates.categoryLabel')}
              // @ts-expect-error - Dropdown selected prop not properly typed
              selected={[templateCategory, setTemplateCategory]}
              items={categoryItems}
              useKeyAsValue
            />
          </Flex>
          <Flex column gap="8px">
            <Flex justify="space-between" align="center">
              <Text size="14px" weight="600">
                {t('editor.serviceTemplates.templateContentLabel')}
              </Text>
              <BackgroundToggle
                $active={isDarkBg}
                onClick={() => setInvertedBg((prev) => !prev)}
                title={t('editor.serviceTemplates.invertBackground')}
              >
                <Icon icon={isDarkBg ? 'moon' : 'sun'} />
                {isDarkBg
                  ? t('editor.serviceTemplates.darkBackground')
                  : t('editor.serviceTemplates.lightBackground')}
              </BackgroundToggle>
            </Flex>
            <Text size="12px" color="gray2">
              {t('editor.serviceTemplates.templateContentDescription')}
            </Text>
          </Flex>
          <Flex
            column
            border="1px solid"
            borderColor="gray4"
            padding="16px"
            minHeight="300px"
            transition="background 0.2s ease"
            background={isDarkBg ? DARK_PREVIEW_BG : LIGHT_PREVIEW_BG}
            style={{
              borderRadius: '4px',
            }}
          >
            <TinyMCE
              setRef={setTinyMCERef}
              caseDetails={caseDetails}
              value=""
              init={{
                height: 300,
                menubar: false,
                statusbar: false,
              }}
            />
          </Flex>
          <Flex justify="flex-end" gap="12px" margin="20px 0 0 0">
            <Button onClick={handleCancel} disabled={isCreating}>
              {t('editor.serviceTemplates.cancelButton')}
            </Button>
            <Button
              primary
              onClick={handleCreateTemplate}
              disabled={isCreating}
            >
              {isCreating
                ? t('editor.serviceTemplates.creatingButton')
                : t('editor.serviceTemplates.createTemplateButton')}
            </Button>
          </Flex>
        </Flex>
      </S.Content>
    </Flex>
  )
}

export default ServiceTemplateCreate
